import "perfect-scrollbar/css/perfect-scrollbar.css";

import classnames from "classnames";
import PerfectScrollbar from "perfect-scrollbar";
import * as React from "react";

import { useNote } from "../noteContext";
import { NoteComposer } from "./note-composer.component";
import { NoteList } from "./note-list.component";
import { useNoteContainerStyles } from "./notes.styles";

interface Props {
    baseId: string;
    children?: React.ReactNode;
    viewOnly?: boolean;
}

export const NotesContainer = React.memo((props: Props) => {
    const classes = useNoteContainerStyles({});
    const [composeClass, setComposerClass] = React.useState(
        classnames(classes.composer, classes.hideBottomShadow)
    );

    const { noteState, noteActions } = useNote();

    const { baseId, viewOnly = false } = props;
    const { notes, currentContext } = noteState;
    const extendedId = `${baseId}Notes`;

    const divAtBottom = React.useRef<HTMLDivElement>(null);
    const listDiv = React.useRef<HTMLDivElement>(null);
    const tick = React.useRef(false);
    const lastScroll = React.useRef(0);

    const ps = React.useRef<PerfectScrollbar>();

    const handleSaveNote = async (note: string) => {
        await noteActions.save(
            note,
            noteState.currentUserId,
            noteState.currentJobId
        );
    };

    const handleIsDirty = React.useCallback(
        (value: boolean) => {
            noteActions.setIsDirty(value);
        },
        [noteActions]
    );

    React.useEffect(() => {
        const div = divAtBottom.current as HTMLElement;
        div && div.scrollIntoView(false); //{ behavior: "smooth", block: "start", inline: "start" });
    }, [notes.length]);

    React.useEffect(() => {
        const div = listDiv.current as HTMLElement;
        ps.current = new PerfectScrollbar(div, {
            suppressScrollX: true,
            suppressScrollY: false,
        });

        div.onscroll = (e: Event) => {
            if (!e.target) return;
            const element = e.target as HTMLElement;
            lastScroll.current = element.scrollTop;
            if (!tick.current) {
                window.requestAnimationFrame(() => {
                    if (lastScroll.current === 0) {
                        setComposerClass(classes.composer);
                    } else if (
                        lastScroll.current + element.offsetHeight >=
                        element.scrollHeight - 4
                    ) {
                        setComposerClass(
                            classnames(
                                classes.hideBottomShadow,
                                classes.composer
                            )
                        );
                    } else {
                        setComposerClass(classes.composer);
                    }
                    tick.current = false;
                });
                tick.current = true;
            }
        };

        return () => {
            div.onscroll = null;
            ps.current && ps.current.destroy();
            ps.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={classes.root} id={`${extendedId}Container`}>
            <div className={classes.list} ref={listDiv}>
                <NoteList baseId={extendedId} notes={notes} />
                <div ref={divAtBottom} style={{ marginBottom: -55 }} />
            </div>
            {!viewOnly ? (
                <div className={composeClass}>
                    <NoteComposer
                        baseId={extendedId}
                        context={currentContext}
                        onSend={handleSaveNote}
                        onSetNoteDirty={handleIsDirty}
                    />
                </div>
            ) : null}
        </div>
    );
});
